import React, { useContext, useState } from "react";
import { ComposableMap, Geographies, Geography, Marker } from "react-simple-maps";
import { geoCentroid } from "d3-geo";
import INDIA_TOPOJSON from "../../india.json";
import { FilterContext } from "../../context/Filter";

const SpareMap = ({ data = [] }) => {
    const [hoverInfo, setHoverInfo] = useState(null);
    const { open } = useContext(FilterContext);

    // ✅ Normalize names (to match TopoJSON)
    const normalizeStateName = (name) => {
        const map = {
            Orissa: "Odisha",
            "Chattisgarh": "Chhattisgarh",
            "NCT of Delhi": "Delhi",
            "Jammu & Kashmir": "Jammu and Kashmir",
            "Andaman & Nicobar Islands": "Andaman and Nicobar Islands",
            "Dadra & Nagar Haveli": "Dadra and Nagar Haveli",
            "Dadra and Nagar Haveli and Daman and Diu": "Dadra and Nagar Haveli and Daman and Diu",
            "Pondicherry": "Puducherry",
        };
        return map[name?.trim()] || name?.trim();
    };

    // ✅ Convert API data to a normalized map safely
    const stateCounts = (data?.reduce((acc, item) => {
        const normalizedName = normalizeStateName(item.key);
        acc[normalizedName] = item.doc_count;
        return acc;
    }, {})) || {};

    // ✅ Compute color scaling safely
    const counts = Object.values(stateCounts || {});
    const minCount = Math.min(...counts, 0);
    const maxCount = Math.max(...counts, 1);

    const lightColor = { r: 224, g: 243, b: 255 };
    const darkColor = { r: 0, g: 64, b: 128 };

    const getColor = (count) => {
        if (!count) return "#E0E0E0";
        const ratio = (count - minCount) / (maxCount - minCount || 1);
        const r = Math.round(lightColor.r + (darkColor.r - lightColor.r) * ratio);
        const g = Math.round(lightColor.g + (darkColor.g - lightColor.g) * ratio);
        const b = Math.round(lightColor.b + (darkColor.b - lightColor.b) * ratio);
        return `rgb(${r},${g},${b})`;
    };

    return (
        <div className="p-2 flex flex-col items-center">
            <div className="w-full flex items-center justify-center relative">
                <ComposableMap
                    projection="geoMercator"
                    projectionConfig={{ center: [80, 22], scale: 800 }}
                    width={700}
                    height={570}
                    style={{ width: "100%", height: "100%" }}
                >
                    <Geographies geography={INDIA_TOPOJSON}>
                        {({ geographies }) =>
                            geographies.map((geo) => {
                                const stateName = geo.properties.st_nm;
                                const count = stateCounts[stateName] || 0;
                                const centroid = geoCentroid(geo);

                                return (
                                    <React.Fragment key={geo.rsmKey}>
                                        <Geography
                                            geography={geo}
                                            onMouseEnter={(event) => {
                                                const { clientX, clientY } = event;
                                                setHoverInfo({
                                                    state: stateName,
                                                    count,
                                                    x: clientX,
                                                    y: clientY,
                                                });
                                            }}
                                            onMouseMove={(event) => {
                                                const { clientX, clientY } = event;
                                                setHoverInfo((prev) => ({
                                                    ...prev,
                                                    x: clientX,
                                                    y: clientY,
                                                }));
                                            }}
                                            onMouseLeave={() => setHoverInfo(null)}
                                            style={{
                                                default: {
                                                    fill: getColor(count),
                                                    stroke: "#000",
                                                    strokeWidth: 0.5,
                                                },
                                                hover: { fill: "#FFCC00" },
                                            }}
                                        />

                                        {/* Marker placeholder (can add text or icons here if needed) */}
                                        {centroid && <Marker coordinates={centroid}></Marker>}
                                    </React.Fragment>
                                );
                            })
                        }
                    </Geographies>
                </ComposableMap>

                {/* ✅ Tooltip */}
                {hoverInfo && (
                    <div
                        className="fixed bg-white p-2 rounded shadow pointer-events-none z-[1000]"
                        style={{ top: hoverInfo.y + 10, left: hoverInfo.x + 10 }}
                    >
                        <p>
                            {hoverInfo.state}: {hoverInfo.count}{" "}
                            {hoverInfo.count === 1 ? "Vehicle" : "Vehicles"}
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SpareMap;
